/**
 * Archive Status Card Component
 *
 * Displays S3 archive configuration and next scheduled archive info.
 */

import { Archive, HardDrive, Calendar, Clock } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import type { ArchiveConfig } from '@/types/archive-management';

interface ArchiveStatusCardProps {
  config: ArchiveConfig;
  nextScheduledArchive: { year: number; month: number } | null;
}

/**
 * Format month/year as readable string
 */
function formatMonthYear(year: number, month: number): string {
  const date = new Date(year, month - 1);
  return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}

/**
 * Archive Status Card
 * Shows S3 bucket configuration, retention policy, and next scheduled archive
 */
export function ArchiveStatusCard({ config, nextScheduledArchive }: ArchiveStatusCardProps) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">Archive Configuration</CardTitle>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
            S3 Storage
          </Badge>
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {/* S3 Bucket */}
          <div className="flex items-center gap-3">
            <HardDrive className="h-4 w-4 text-muted-foreground" />
            <div>
              <p className="text-sm font-medium truncate max-w-[180px]" title={config.s3Bucket}>
                {config.s3Bucket}
              </p>
              <p className="text-xs text-muted-foreground">S3 Bucket</p>
            </div>
          </div>

          {/* S3 Prefix */}
          <div className="flex items-center gap-3">
            <Archive className="h-4 w-4 text-muted-foreground" />
            <div>
              <p className="text-sm font-medium font-mono">{config.s3Prefix}</p>
              <p className="text-xs text-muted-foreground">S3 Prefix</p>
            </div>
          </div>

          {/* Retention Days */}
          <div className="flex items-center gap-3">
            <Clock className="h-4 w-4 text-muted-foreground" />
            <div>
              <p className="text-sm font-medium">{config.retentionDays} days</p>
              <p className="text-xs text-muted-foreground">DB Retention</p>
            </div>
          </div>

          {/* Next Scheduled Archive */}
          <div className="flex items-center gap-3">
            <Calendar className="h-4 w-4 text-muted-foreground" />
            <div>
              <p className="text-sm font-medium">
                {nextScheduledArchive
                  ? formatMonthYear(nextScheduledArchive.year, nextScheduledArchive.month)
                  : 'Not scheduled'}
              </p>
              <p className="text-xs text-muted-foreground">Next Archive</p>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
